import React, { useState, useId, useEffect, useMemo } from 'react'
import Select from 'components/QuestionnaireRequest/AdminNew/components/Select'
import CurrencyInput from 'react-currency-input-field'
import { useForm, Controller } from 'react-hook-form'
import { nanoid } from '@reduxjs/toolkit'
import axios from 'lib/axios'
import clsx from 'clsx'
import { pick } from 'lodash-es'
import { isSameData } from 'components/QuestionnaireRequest/Property/Tabs/Pricing/utilities'

const FORM_TYPES = [
  { 'value': 'generic', 'label': 'generic' },
  { 'value': 'lender', 'label': 'lender' },
  { 'value': 'attorney', 'label': 'attorney / due dilligence' },
  { 'value': 'appraiser', 'label': 'appraiser' },
  { 'value': 'other', 'label': 'other' },
]

const LabelField = ({ register, error, watch }) => {
  const isDefault = watch('default')
  return (
    <div className="questionnaire-admin-pricing-input-container">
      <input
        type="text"
        {...register('name', {
          required: 'Label is required',
          validate: (value) => value.trim() !== '' || 'Label cannot be empty',
        })}
        readOnly={isDefault}
      />
      {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{error.message}</div>}
    </div>
  )
}

const MoneyField = ({ control, register, watch, error, trigger }) => {
  const checkboxId = useId()
  const dynamicPrice = watch('dynamic_price')

  const dynamicPriceRegister = register('dynamic_price')

  return (
    <div className="questionnaire-admin-pricing-input-container">
      <div className="dome-d-flex dome-justify-between dome-gap3">
        <Controller
          name="price"
          control={control}
          rules={{
            validate: {
              custom: (value) => {
                if (!dynamicPrice && !value) {
                  return 'Price is required when not variable'
                }
                if (!dynamicPrice && value && !String(value).match(/^\d+(\.\d{1,2})?$/)) {
                  return 'Invalid price format'
                }
                return true
              },
            },
          }}
          render={({ field: { onChange, value, onBlur } }) => (
            <CurrencyInput
              placeholder="$"
              defaultValue={value}
              allowDecimals={false}
              decimalScale={0}
              allowNegativeValue={false}
              prefix="$"
              step={1}
              onValueChange={onChange}
              disabled={dynamicPrice}
              onBlur={onBlur}
            />
          )}
        />

        <div className="dome-d-flex dome-gap3 dome-align-center" style={{ flexShrink: 0 }}>
          <input
            type="checkbox"
            id={checkboxId}
            style={{ margin: 0 }}
            {...dynamicPriceRegister}
            onChange={(e) => {
              dynamicPriceRegister.onChange(e)
              setTimeout(() => trigger('price'), 1)
            }}
          />
          <label htmlFor={checkboxId} style={{ margin: 0 }}>
            variable
          </label>
        </div>
      </div>
      {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{error.message}</div>}
    </div>
  )
}

const TableHead = () => {
  return (
    <thead>
      <tr>
        <th className="col-xs-3">
          <span>price label</span>
        </th>
        <th className="col-xs-3">
          <span>price</span>
        </th>
        <th className="col-xs-3">
          <span>type</span>
        </th>
        <th className="col-xs-3">
          <span>actions</span>
        </th>
      </tr>
    </thead>
  )
}

const PriceItemRow = ({ price, organizationId, onUpdate, onRemove }) => {
  const [isSaving, setIsSaving] = useState(false)

  const { register, control, handleSubmit, watch, formState, setError, clearErrors, trigger, reset, getValues } =
    useForm({
      defaultValues: {
        name: price.name || '',
        price: price.price,
        dynamic_price: price.dynamic_price || false,
        selected_form_types: price.selected_form_types || [],
        default: price.default || false,
      },
      mode: 'onChange',
    })

  const { errors, dirtyFields } = formState

  const watchValues = watch()
  const isDirty = useMemo(() => {
    for (const key in formState.dirtyFields) {
      const currentValue = getValues(key)
      const defaultValue = price[key]

      if (!isSameData(currentValue, defaultValue)) {
        return true
      }
    }
    return false
  }, [watchValues, formState, getValues])

  useEffect(() => {
    reset(pick(price, ['name', 'price', 'dynamic_price', 'selected_form_types', 'default']))
  }, [price, reset])

  const isDefault = watch('default')

  const onSubmit = async (data) => {
    setIsSaving(true)
    try {
      const payload = {
        org_price: {
          name: data.name,
          price: data.dynamic_price ? null : data.price,
          dynamic_price: data.dynamic_price ? 1 : 0,
          selected_form_types: data.selected_form_types,
          default: data.default ? 1 : 0,
        },
      }

      let response
      if (price.is_new) {
        const url = `/super/organizations/${organizationId}/questionnaires/pricing`
        response = await axios.post(url, payload)
        onUpdate(price.id, { ...response.data, is_new: false })
      } else {
        const url = `/super/organizations/${organizationId}/questionnaires/pricing/${price.id}`
        response = await axios.put(url, payload)
        onUpdate(price.id, { ...price, ...response.data })
      }
    } catch (error) {
      console.error('Error saving price:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemove = async () => {
    if (price.is_new) {
      onRemove(price.id)
      return
    }

    const confirmed = window.confirm(`Are you sure you want to remove ${price.name} from the list of prices?`)
    if (!confirmed) return

    setIsSaving(true)
    try {
      const url = `/super/organizations/${organizationId}/questionnaires/pricing/${price.id}`
      await axios.delete(url, { data: { price: price.id } })
      onRemove(price.id)
    } catch (error) {
      console.error('Error removing price:', error)
      setIsSaving(false)
    }
  }

  const handleFormTypesChange = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) {
      setError('selected_form_types', {
        type: 'manual',
        message: 'At least one type must be selected',
      })
    } else {
      clearErrors('selected_form_types')
    }
  }

  const hasErrors = Object.keys(errors).length > 0
  const isDisabled = !isDirty || hasErrors || isSaving

  return (
    <tr>
      <td>
        <LabelField register={register} watch={watch} error={errors.name} />
      </td>
      <td>
        <MoneyField control={control} register={register} watch={watch} trigger={trigger} error={errors.price} />
      </td>
      <td>
        <div>
          <Controller
            name="selected_form_types"
            control={control}
            rules={{
              validate: (value) => (value && value.length > 0) || 'At least one type must be selected',
            }}
            render={({ field: { onChange, value, onBlur } }) => (
              <Select
                multiple={true}
                options={FORM_TYPES}
                defaultSelectedItems={value}
                onChange={(selectedItems) => {
                  onChange(selectedItems)
                  handleFormTypesChange(selectedItems)
                }}
                onClose={() => onBlur()}
                selectAll={true}
                selectAllOptionLabel="select all types"
                selectAllDisplay="all types"
                label="select type"
                disabled={isDefault}
              />
            )}
          />
          {errors.selected_form_types && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.selected_form_types.message}</div>
          )}
        </div>
      </td>
      <td>
        <div className="dome-d-flex dome-gap3">
          <button
            className={clsx(`dome-btn dome-btn-base dome-btn-go-green`, isDisabled && 'dome-btn-disabled')}
            onClick={handleSubmit(onSubmit)}
            disabled={isDisabled}
          >
            save
          </button>
          <button
            className={clsx(
              `dome-btn dome-btn-base dome-btn-red-stroke`,
              (isDefault || isSaving) && 'dome-btn-disabled'
            )}
            onClick={handleRemove}
            disabled={isDefault || isSaving}
          >
            remove
          </button>
        </div>
      </td>
    </tr>
  )
}

const TableBody = ({ prices, organizationId, onUpdate, onRemove }) => {
  if (prices.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={4}>Click "+ default price" to create the default price</td>
        </tr>
      </tbody>
    )
  }

  return (
    <tbody>
      {prices.map((price) => (
        <PriceItemRow
          key={price.id}
          price={price}
          organizationId={organizationId}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      ))}
    </tbody>
  )
}

export default function Pricing({ organizationId, prices, onAddNewPrice, onUpdatePrice, onRemovePrice }) {
  const addNewPrice = () => {
    const newPrice = {
      id: nanoid(),
      is_new: true,
      name: prices.length === 0 ? 'Default' : '',
      price: null,
      dynamic_price: false,
      selected_form_types: FORM_TYPES.map((item) => item.value),
      default: prices.length === 0,
    }
    onAddNewPrice(newPrice)
  }

  const handleUpdatePrice = (oldId, updatedPrice) => {
    onUpdatePrice({ oldId, updatedPrice })
  }

  const handleRemovePrice = (priceId) => {
    onRemovePrice(priceId)
  }

  return (
    <>
      <table className="dome-table dome-table-striped dome-full-width">
        <TableHead />
        <TableBody
          prices={prices}
          organizationId={organizationId}
          onUpdate={handleUpdatePrice}
          onRemove={handleRemovePrice}
        />
      </table>
      <div className="dome-d-flex dome-justify-between">
        <button type="button" className="dome-btn dome-btn-base dome-btn-aqua-stroke" onClick={addNewPrice}>
          {prices.length === 0 ? '+ default price' : '+ price'}
        </button>
      </div>
    </>
  )
}
